export const profile = {
  name: "Simon Stead",
  role: "AI & Technology Leader · CPTO",
  phone: "07455 174786",
  email: "steady@steadtec.com",
  site: "www.steadtec.com",
  summary:
    "AI and technology leader with over a decade building and scaling engineering organisations and shipping production AI systems. I set AI and product strategy, own the roadmap end to end, and stay hands-on: generative AI, LLM and agentic pipelines, MLOps, machine-learning forecasting, and responsible AI governance.",
  summary2:
    "AI acceleration has made me a full-spectrum operator. I work across the whole product and delivery lifecycle: user research, service design, UX and design systems, full-stack and platform engineering, data science, management consulting, agile coaching and Team Topologies, growth, and commercial and financial modelling. Small, T-shaped, AI-augmented teams now outpace much larger conventional engineering capability. I build the strategy, tooling, and culture that get them there.",
  summary3:
    "I scaled an office from 10 to 90 people, built platforms handling 300M+ daily transactions, and I am now turning a consultancy into an AI-native delivery business at a defensible 6x delivery acceleration. My work spans retail, financial services, gaming and the public sector. I turn hard technology into commercial outcomes for stakeholders and boards. Active SC clearance.",
};

export const howIWork = [
  "I form and lead lean, cross-disciplinary teams, or operate solo across the full stack, and use AI to compress discovery-to-production timelines by up to 6x.",
  "I favour evidence-gated, killable bets over open-ended staffing: continuous discovery, thin slices to production, and architecture that keeps options open (event sourcing, deterministic replay, privacy-by-design).",
  "I stay hands-on. I write production code, build the AI tooling myself when a team needs it, and set the standards. Strategy stays grounded in what ships.",
];

export const skills = [
  {
    t: "AI & Machine Learning",
    d: "Generative AI and LLM pipelines: prompt engineering, orchestration, structured/schema-constrained generation, RAG, vector databases, embeddings, semantic search; agentic and multi-agent systems, event-triggered agentic pipelines; image-generation and multi-model pipelines; applied NLP, speech-to-text, transcription, summarisation; time-series forecasting (Darts, SARIMAX), gradient boosting (XGBoost), churn/propensity modelling; MLOps and model serving on Kubernetes; AI evaluation, guardrails, Responsible AI governance. Model-agnostic across Claude (Anthropic, primary), Gemini, GPT, and Llama. I build custom AI tooling myself when a team needs it, and develop primarily in Claude Code.",
  },
  {
    t: "Leadership & Strategy",
    d: "AI strategy and roadmap, product and technology strategy, AI operating model and governance, KPI/ROI definition, budget and P&L ownership, cross-functional leadership, stakeholder and board communication, hiring and team scaling, agile transformation.",
  },
  {
    t: "Full-Spectrum Delivery",
    d: "User research, service design, UX and design systems, full-stack engineering, infrastructure and platform engineering, data science, management consulting, agile coaching, Team Topologies, growth strategy, and financial/commercial modelling. Discovery to production, at any scale.",
  },
  {
    t: "Engineering",
    d: "Node.js (Express, Koa), Python (Django, Flask), TypeScript, React (Next.js, hooks, Redux), SQL, gRPC, GraphQL, responsive CSS/SCSS, accessibility (WCAG). MongoDB, PostgreSQL, MySQL, DynamoDB, DocumentDB. Containers, microservices, serverless, event-driven and event-sourced systems, high availability, observability.",
  },
  {
    t: "Tooling & Practice",
    d: "Git, Docker, Kubernetes, AWS, Google Cloud, Microsoft Azure, Terraform, Cypress; XP, functional programming, TDD/BDD, CI/CD. Product discovery, prototyping, conversion-rate optimisation, performance and load testing, teaching, mentoring, public speaking.",
  },
];

export const experience = [
  {
    org: "Hippo Digital",
    role: "Principal Engineer",
    period: "2024 – Present",
    context: "Public-sector consultancy.",
    bullets: [
      "Lead AI innovation across the business with the Chief Digital Officer, Chief Delivery Officer, and Director of AI, turning Hippo from a traditional consultancy into an AI-native delivery business. Define how LLM-assisted delivery reshapes rate cards, margins, and commercial models; run enablement workshops and training; prototype AI-assisted delivery methods before scaling them across engagements. Early results: 6x faster delivery and roughly 80% more time freed for decision-making.",
      "Ran an AI hackathon for Police Scotland using speech-to-text and generative AI to automate transcription, summarisation, and insight extraction from 999 calls, opening new business in the public-safety sector.",
      "Line-manage 6 engineers. Mentor, run career development, and raise the technical bar.",
      "Founded and chair the Engineering Community of Practice, driving standards and cross-team practice.",
      "Lead the technical arm of bids worth £15–20m, shaping statements of work with delivery partners.",
    ],
  },
  {
    org: "BBC Bitesize Primary",
    role: "Software Engineering Team Lead",
    sub: "Client engagement via Hippo Digital, reporting to BBC Head of Product & Innovation",
    period: "2026 – Present",
    bullets: [
      "Set and own the engineering direction for a re-platform of one of the UK's largest children's education services (1.7m primary pupils a week; 61% weekly reach). Drive architecture, delivery model, and product decisions from discovery to working alpha.",
      "Architected an event-sourced platform (CQRS, deterministic replay) built to scale to 20 billion transactions a year at a fraction of a penny per child, proven end to end.",
      "Reframed strategy from acquisition to retention on hard evidence, winning the mandate and roadmap for an experience-layer rebuild.",
      "Rebuilt the primary design language as a reusable token system over a shared, accessible (WCAG-AA) component kit. A new subject drops from a design project to a config change.",
      "Aligned engineering, product, editorial, and design around one operating model and a continuous-discovery, evidence-gated delivery model. The in-house research programme scored NPS +19 with curriculum-aware users.",
    ],
    stack: "TypeScript, React 19, Node.js, PostgreSQL, event sourcing/CQRS, xAPI, AWS",
  },
  {
    org: "BBC",
    role: "Lead Service Designer & Technologist",
    sub: "Games Platform Discovery & Prototype, via Hippo Digital",
    period: "4 weeks",
    bullets: [
      "Led an end-to-end discovery (research, service design, and a working build, no handoffs) into whether the BBC could take on the New York Times' daily-games retention habit.",
      "Built a full games platform and 30 playable games inside the timebox, using generative AI as a build multiplier. Real, playable products in front of users drove a commissioning decision on evidence.",
      "Designed it on BBC-native infrastructure (AWS, WebCore, the BBC design system) as a credible route to production, backed by ~20 research sessions and stakeholder workshops.",
    ],
  },
  {
    org: "NHS Digital",
    role: "Lead Architect & Delivery",
    sub: "Proxy Validated Relationships Service, via Hippo Digital",
    period: "2025",
    bullets: [
      "Led architecture and delivery of the NHS Proxy Validated Relationships Service, a national platform letting authorised people manage healthcare on behalf of patients. Directed 8–10 engineers building the service and its public-facing API on a serverless, event-driven microservices architecture supporting 700k+ proxy role migrations, targeting national adoption in March 2026 via GPIT supplier integrations.",
      "Fully serverless on AWS Lambda (30+ functions), Step Functions, and EventBridge, at 98% uptime SLA.",
      "FHIR R4 APIs integrating 6+ NHS national systems including PDS, APIM, and GP systems.",
      "Security framework: OAuth 2.0, JWT, KMS encryption, full audit trails.",
      "Infrastructure-as-Code in Terraform across 11 modular stacks and multiple environments.",
      "80%+ test coverage, automated CI/CD, sub-200ms response times under load.",
    ],
    stack: "Python 3.13, AWS (Lambda, DynamoDB, API Gateway, EventBridge), Terraform, FHIR R4, OAuth 2.0",
  },
  {
    org: "Gamstop Group",
    role: "Principal Engineer",
    sub: "via Hippo Digital",
    period: "2024",
    bullets: [
      "Turned a struggling feature team into a high-performance agile delivery unit under constant pressure.",
      "Consolidated 8 siloed platforms into one scalable, high-availability portal for operators across the UK.",
      "Engineered a system handling 300+ million daily transactions, connecting every gambling operator in the country, with zero-downtime operation and disaster recovery proven by load and stress testing.",
      "Built CI/CD, automated testing, and monitoring, raising release velocity and cutting downtime.",
    ],
    stack: "TypeScript (React, Next.js), AWS (RDS, DMS, Lambda, API Gateway, ECS/Fargate, SQS, SES), Node.js, Java",
  },
  {
    org: "Quiltt Ltd",
    role: "Chief Product & Technology Officer (CPTO)",
    period: "2022 – 2023",
    context: "SaaS AI-driven sales forecasting and analytics platform.",
    bullets: [
      "Co-founded the company and built its entire machine-learning and AI capability (Python, Darts, SARIMAX, XGBoost), the forecasting software, the APIs (TypeScript, Deno), and the frontend (TypeScript, Next.js).",
      "Owned architecture and platform: a highly available, scalable Kubernetes estate on AWS and DigitalOcean, plus CI/CD and automated testing.",
    ],
  },
  {
    org: "AND Digital",
    role: "Principal Engineer & Consultant",
    period: "2019 – 2022",
    link: "and.digital",
    bullets: [
      "Scaled the office from 10 to 90 people, including 30+ developers. Mentored engineers at every level and taught regular TDD and React workshops, internally and to clients.",
      "Mortgage Hub, Tech Lead: mortgage origination platform, 3 user portals in React/TypeScript, 20+ microservices on Kubernetes (Hasura, Apache Kafka, GraphQL, PostgreSQL, Java), full GitOps CI/CD.",
      "Lloyds Banking Group, Tech Lead of 11: event-driven pensions platform for Scottish Widows handling billions in transactions. Lifted team velocity 40% and cut deployment from days to hours.",
      "Boohoo Group: led a newly remote team through COVID to ship an internal ordering app in 3 months, saving £500k a year and securing follow-on work. Built a CEO reporting portal in 4 weeks.",
      "Missguided: built machine learning to predict customer churn, winning a £3.9m two-year build-partner contract.",
      "NuGamma: AI-powered restaurant stock and staff prediction dashboard, 10/10 NPS.",
      "Designed the interview process and engineering standards for new UK offices, and trained and mentored across the Manchester office.",
    ],
  },
  {
    org: "Footasylum Plc",
    role: "Technical Architect",
    period: "2019",
    bullets: [
      "Lead architect across a distributed team delivering a re-platformed checkout carrying £90m annual revenue.",
      "Introduced production-readiness processes, set technical direction across the estate, and drove culture change in the development community.",
    ],
  },
  {
    org: "Make Thread Ltd",
    role: "Freelance",
    period: "2018 – 2023",
    link: "makethread.com",
    bullets: [
      "Built a sustainable social-fashion marketplace with e-commerce and crowdfunding, part Kickstarter and part social network. RESTful API, admin interface, React, MongoDB, Node.js.",
    ],
  },
  {
    org: "Co-op Digital",
    role: "Senior Software Engineer",
    period: "2016 – 2018",
    context:
      "Co-op Digital was founded by the GOV.UK/GDS team (Mike Bracken, Ben Terrett, now Public Digital). I learned accessible, user-needs service design and Extreme Programming, TDD, and clean code from the engineers around me.",
    bullets: [
      "Tech Lead on local.co.uk: a full marketplace, seller interface, and API in 14 weeks for local artisans, built in Python and React on AWS.",
      "Shifts: shift and schedule visibility for Co-op food-store colleagues, live to over 50,000 staff.",
      "Store Dashboard: cut paper-based work with a Ruby on Rails backend, a Go API, and real-time push to a React app.",
      "Product Finder: replaced a 10-million-row Salesforce spreadsheet and turned a 20-minute, two-call job into seconds.",
    ],
  },
  {
    org: "MPP Global",
    role: "Software Engineer",
    period: "2015 – 2016",
    context: "Team of 5 in a 20m+ line codebase (Node.js, C#, ASP.NET, T-SQL).",
    bullets: [
      "Delivered a critical i18n retrofit to the CRM, unlocking a multi-million-pound deal.",
      "Built a cryptographic scheme that sharply cut storage in the offers and voucher system.",
      "Stood up a 4-stage, high-availability platform in a new AWS region for clients in the USA and Japan.",
    ],
  },
];

export const ventures = [
  {
    org: "Rise of Realms",
    role: "Founder, AI & Product Lead",
    period: "2024 – Present",
    link: "rise-of-realms.com",
    bullets: [
      "Built a full generative-AI production pipeline end to end, orchestrating Claude and image-generation models from narrative structure to finished card art: a repeatable, agentic system that produces content at studio scale, solo.",
      "Generated 2,000 distinct card units, each with its own mechanics and lore, held to consistent balance.",
      "Designed a fair, unified commercial model: $1 flat per base card, single digital-and-physical ownership, print-on-demand in 100+ languages.",
    ],
  },
  {
    org: "Crisis Angels",
    role: "Founder & CPTO",
    period: "2025 – Present",
    link: "crisis-angels.com",
    bullets: [
      "Shipped applied AI in a high-stakes clinical setting: an adaptive wellness score over five signals, AI-generated weekly summaries, and automated trigger identification, built for safety and interpretability.",
      "Implemented the clinically validated Stanley-Brown safety plan and one-tap crisis activation with follow-ups at days 1, 3, 7, and 14.",
      "Built privacy in from the start: end-to-end encryption, per-metric consent, UK data residency, DTAC alignment. No data sales, no advertising.",
    ],
  },
  {
    org: "Tuckbox Games",
    role: "Founder, Technology & Product Lead",
    period: "2025 – Present",
    link: "tuckboxgames.com",
    bullets: [
      "An independent studio building Sabrina (spelling practice that finds the rule) and Buddies (a character builder with learning reward systems), procedural, rule-based games that diagnose specific learning gaps rather than just scoring performance.",
    ],
  },
  {
    org: "Huile",
    role: "Founder, Formulation, Product & Engineering",
    period: "2026 – Present",
    link: "huile-skincare.com",
    bullets: [
      "Personally formulate and produce the six-SKU luxury face-oil line (£65–£70), built around a signature astaxanthin formulation with cacay oil and bakuchiol.",
      "Built the digital product and commerce platform end to end on Next.js, and own product and merchandising.",
    ],
  },
  {
    org: "Wishing Well Woods",
    role: "Founder, Technology & Product Lead",
    period: "2026 – Present",
    link: "wishingwellwoods.com",
    bullets: [
      "Built the Next.js store end to end for a handcrafted laser-cut birch-plywood goods brand: catalog, cart, and fulfilment logic under a small-batch, sustainably sourced model.",
    ],
  },
  {
    org: "Unique Tarot Co",
    role: "Founder & Creator",
    period: "2020 – Present",
    bullets: [
      "Built a custom manufacturing pipeline for one-of-a-kind, hand-finished tarot cards across multiple product lines, and grew a direct-to-consumer channel on Instagram.",
    ],
  },
];

export const education = [
  {
    org: "University of York",
    role: "MMath Mathematics, First Class Honours",
    period: "2011 – 2015",
    detail:
      "Algebraic geometry, topology, complex analysis, algebraic number theory. Modules in C++, Python, R, MAPLE, Mathematica, HTML, CSS, JavaScript.",
  },
  {
    org: "A Levels",
    role: "A* A B B: Maths, Further Maths, Chemistry, Physics",
    period: "2009 – 2011",
  },
  {
    org: "GCSEs",
    role: "8 A* and 2 A",
    period: "2005 – 2009",
  },
];

export const awards = [
  "Big Chip 2018, Best Mobile Application: Shifts, Co-op Digital.",
  "Big Chip 2018, Best User Experience: Shifts, Co-op Digital.",
];
